import React, { useRef, useState } from 'react';
import * as S from './DigitInputs.styled';
import { onlyNumbers } from '@/utils/helpers';
import {
  PHONE_NUMBER_SUBSCRIBER_LENGTH,
  PHONE_NUMBER_WILDCARD,
} from '@/features/PhoneNumberSearch/constants';

type DigitInputsProps = {
  digits: string[];
  onDigitChange: (index: number, value: string) => void;
  onSubmit: () => void;
  isHoverSuppressed: boolean;
  setIsHoverSuppressed: (value: boolean) => void;
};

export const DigitInputs = ({
  digits,
  onDigitChange,
  onSubmit,
  isHoverSuppressed,
  setIsHoverSuppressed,
}: DigitInputsProps) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  function focusInput(index: number) {
    setIsHoverSuppressed(true);
    inputRefs.current[index]?.focus();
  }

  function applyDigit(index: number, value: string) {
    onDigitChange(index, value);

    if (index < PHONE_NUMBER_SUBSCRIBER_LENGTH - 1) {
      focusInput(index + 1);
      return;
    }

    inputRefs.current[index]?.select();
  }

  function handleChange(index: number, rawValue: string) {
    const value = onlyNumbers(rawValue).slice(-1);

    if (!value) {
      onDigitChange(index, '');
      return;
    }

    applyDigit(index, value);
  }

  function handleKeyDown(
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) {
    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      applyDigit(index, event.key);
      return;
    }

    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      onDigitChange(index - 1, '');
      focusInput(index - 1);
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (
      event.key === 'ArrowRight' &&
      index < PHONE_NUMBER_SUBSCRIBER_LENGTH - 1
    ) {
      event.preventDefault();
      focusInput(index + 1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  }

  function handlePaste(
    startIndex: number,
    event: React.ClipboardEvent<HTMLInputElement>
  ) {
    event.preventDefault();

    const pastedDigits = onlyNumbers(event.clipboardData.getData('text'));

    if (!pastedDigits) return;

    const digitsToFill = pastedDigits.slice(
      0,
      PHONE_NUMBER_SUBSCRIBER_LENGTH - startIndex
    );
    digitsToFill.split('').forEach((digit, offset) => {
      onDigitChange(startIndex + offset, digit);
    });
    focusInput(startIndex + digitsToFill.length - 1);
  }

  function handleFocus(
    index: number,
    event: React.FocusEvent<HTMLInputElement>
  ) {
    setFocusedIndex(index);
    event.target.select();
  }

  function handlePointerDown(event: React.PointerEvent<HTMLInputElement>) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    event.preventDefault();
    const input = event.currentTarget;
    input.focus();
    input.select();
  }

  function handlePointerLeave(event: React.PointerEvent<HTMLInputElement>) {
    if (event.pointerType === 'mouse') {
      setIsHoverSuppressed(false);
    }
  }

  function renderDigitInput(index: number) {
    const digit = digits[index];
    const isFocused = focusedIndex === index;

    return (
      <S.DigitInput
        key={index}
        ref={element => {
          inputRefs.current[index] = element;
        }}
        value={digit}
        placeholder={isFocused ? '' : PHONE_NUMBER_WILDCARD}
        aria-label={`${index + 1}/7`}
        $isFocused={isFocused}
        $isHoverSuppressed={isHoverSuppressed}
        onChange={event => handleChange(index, event.target.value)}
        onKeyDown={event => handleKeyDown(index, event)}
        onPaste={event => handlePaste(index, event)}
        onFocus={event => handleFocus(index, event)}
        onBlur={() => setFocusedIndex(null)}
        onPointerDown={handlePointerDown}
        onPointerLeave={handlePointerLeave}
      />
    );
  }

  return (
    <>
      <S.SubRow>{[0, 1, 2].map(renderDigitInput)}</S.SubRow>
      <S.SubRow $isSmall>{[3, 4].map(renderDigitInput)}</S.SubRow>
      <S.SubRow $isSmall>{[5, 6].map(renderDigitInput)}</S.SubRow>
    </>
  );
};
